import { useEffect, useMemo, useRef, useState } from 'react';
import definitionsData from '@/data/definitions.json';
import type { GlossaryEntry } from '@/types';
import { GlossaryEntryView, expansionStats } from './GlossaryEntryView';
import styles from './GlossaryReaderPage.module.css';
import { PrintLink } from './PrintLink';
import { TourLink } from './TourLink';
import { GuidedTour, buildGlossarySteps } from './WelcomeTour';
const GLOSSARY_TOUR_KEY = 'aml_tour_glossary_dismissed';
import appHeaderStyles from './AppHeader.module.css';

const GLOSSARY = (definitionsData as any).entries as GlossaryEntry[];
const GLOSSARY_LETTERS = new Set(GLOSSARY.map((e) => (e.term[0] || '').toUpperCase()));
const GLOSSARY_STATS = expansionStats(GLOSSARY);

type Props = { initialEntryId?: string };

export function GlossaryReaderPage({ initialEntryId }: Props) {
  const [query, setQuery] = useState('');
  const [showTour, setShowTour] = useState(false); // secondary tours never auto-open — only via the Tour link
  const [letter, setLetter] = useState('All');
  const [expandedSteps, setExpandedSteps] = useState<Set<string>>(new Set());
  const entryRefs = useRef<Record<string, HTMLDivElement | null>>({});

  // Update page title for this stand-alone page
  useEffect(() => {
    const prev = document.title;
    document.title = 'Glossary — AML Navigator';
    return () => { document.title = prev; };
  }, []);

  // Scroll to initial entry if provided
  useEffect(() => {
    if (!initialEntryId) return;
    const t = setTimeout(() => {
      const el = entryRefs.current[initialEntryId];
      if (el) el.scrollIntoView({ block: 'start', behavior: 'smooth' });
    }, 100);
    return () => clearTimeout(t);
  }, [initialEntryId]);

  const toggleStep = (entryId: string, label: string) => {
    setExpandedSteps((prev) => {
      const next = new Set(prev);
      const key = `${entryId}:${label}`;
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };
  const isStepExpanded = (entryId: string, label: string) =>
    expandedSteps.has(`${entryId}:${label}`);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    const base = GLOSSARY.filter((e) => {
      if (letter !== 'All' && (e.term[0] || '').toUpperCase() !== letter) return false;
      if (!q) return true;
      return (
        e.term.toLowerCase().includes(q) ||
        (e.acronym || '').toLowerCase().includes(q) ||
        (e.gloss || '').toLowerCase().includes(q) ||
        (e.officialText || '').toLowerCase().includes(q)
      );
    });
    if (!q) return base;
    // Relevance ranking: name matches rank above definition-text-only matches, so a query
    // like "oblig" surfaces "obliged entity" first rather than every entry that merely
    // mentions it in its gloss/officialText.
    const score = (e: typeof base[number]) => {
      const t = e.term.toLowerCase();
      const a = (e.acronym || '').toLowerCase();
      if (t === q) return 0;
      if (t.startsWith(q)) return 1;
      if (t.includes(q)) return 2;
      if (a === q || a.startsWith(q)) return 3;
      if (a.includes(q)) return 4;
      return 5; // matched only in gloss / officialText
    };
    return [...base].sort((x, y) => score(x) - score(y) || x.term.localeCompare(y.term));
  }, [query, letter]);

  return (
    <div className={styles.page}>
      <GuidedTour open={showTour} onClose={() => setShowTour(false)} steps={buildGlossarySteps()} storageKey={GLOSSARY_TOUR_KEY} />
      <header className={styles.header}>
        <div className={styles.headerInner}>
          <div className={styles.headerLeft}>
            <h1 className={styles.title}>Glossary</h1>
            <p className={styles.subtitle}>
              EU AML/CFT package — definitions and acronyms
            </p>
          </div>
          <div className={styles.headerRight}>
            <TourLink onClick={() => setShowTour(true)} />
            <PrintLink />
            <a className={styles.backLink} href={window.location.origin + window.location.pathname} title="Back to AML Navigator">
              ← Back to AML Navigator
            </a>
          </div>
        </div>
      </header>

      <div className={styles.infoBar} data-tour="gl-info">
        <span><strong>{GLOSSARY.length}</strong> entries</span>
        {GLOSSARY_STATS.entriesWithExp > 0 && (
          <>
            <span className={styles.infoSep}>·</span>
            <span>
              <strong>{GLOSSARY_STATS.entriesWithExp}</strong> with nested-reference notes
              ({GLOSSARY_STATS.totalVerbatim} verbatim verified via EUR-Lex, up to {GLOSSARY_STATS.maxDepthSeen} levels deep)
            </span>
          </>
        )}
      </div>

      <div className={styles.tools} data-tour="gl-tools">
        <input
          className={styles.search}
          placeholder="Search term, acronym or definition…"
          value={query}
          onChange={e => { const v = e.target.value; setQuery(v); if (v.trim()) setLetter('All'); }}
          autoFocus
        />
        <div className={styles.azRow}>
          <button
            className={`${appHeaderStyles.az} ${appHeaderStyles.azAll} ${letter === 'All' ? appHeaderStyles.azActive : ''}`}
            onClick={() => setLetter('All')}
          >All</button>
          {Array.from({ length: 26 }, (_, i) => String.fromCharCode(65 + i)).map(L => {
            const has = GLOSSARY_LETTERS.has(L);
            return (
              <button
                key={L}
                className={`${appHeaderStyles.az} ${!has ? appHeaderStyles.azOff : ''} ${letter === L ? appHeaderStyles.azActive : ''}`}
                disabled={!has}
                onClick={() => has && setLetter(L)}
              >{L}</button>
            );
          })}
        </div>
        <span className={styles.count}>{filtered.length} of {GLOSSARY.length}</span>
      </div>

      <main className={styles.list}>
        {filtered.length === 0 && <div className={styles.empty}>No matching entries.</div>}
        {filtered.map(e => (
          <GlossaryEntryView
            key={e.id}
            entry={e}
            isStepExpanded={isStepExpanded}
            toggleStep={toggleStep}
            entryRef={(el) => { entryRefs.current[e.id] = el; }}
            focused={initialEntryId === e.id}
          />
        ))}
      </main>

      <footer className={styles.footer}>
        AML Navigator · Glossary · {GLOSSARY.length} entries
      </footer>
    </div>
  );
}
